/**
 * Animation table for the character sheet.
 *
 * Sheet contract (see docs/sprite-spec.md): a 4x4 grid of 32x32 frames.
 * Rows are facings in the order down / left / right / up. Columns are the
 * walk cycle, with column 0 doubling as the idle pose.
 *
 * Frame timing is in MILLISECONDS, not game-loop frames. The reference engine
 * counts frames (`animationFrameLimit`), which makes animation speed drift
 * with display refresh rate — the same bug class the fixed-timestep loop
 * fixes for movement. See docs/canvas-engine-reference.md.
 */

/** Row index per facing. Must match the sheet's row order. */
export const ROW = {
  down: 0,
  left: 1,
  right: 2,
  up: 3,
};

export const FACINGS = Object.keys(ROW);

/** Milliseconds each walk frame is held. Lower = faster stride. */
export const WALK_FRAME_MS = 130;

/**
 * name -> { frames: [[col, row], ...], frameMs }
 *
 * Built from ROW rather than hand-written so a row-order change is a
 * one-line edit instead of eight.
 */
export const ANIMATIONS = FACINGS.reduce((table, facing) => {
  const row = ROW[facing];
  return {
    ...table,
    [`idle-${facing}`]: {
      frames: [[0, row]],
      frameMs: Infinity, // single frame; never advances
    },
    [`walk-${facing}`]: {
      // 0,1,0,3 rather than 0,1,2,3 — the classic 4-step cycle passes through
      // the neutral pose between contact poses, which is what reads as a walk.
      frames: [[0, row], [1, row], [0, row], [3, row]],
      frameMs: WALK_FRAME_MS,
    },
  };
}, {});

/** Pick an animation from authoritative server state. */
export function animationFor(facing, moving) {
  const dir = ROW[facing] === undefined ? 'down' : facing;
  return `${moving ? 'walk' : 'idle'}-${dir}`;
}

export default ANIMATIONS;
